import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { isArray, last } from 'lodash-es';
import { IHttpResponse, UserRole } from '../models';
import { join } from '../utils/common';

export class CcsSentryError extends Error {
  sentryEventId?: string;
  uiMessage?: string;

  constructor(message: string) {
    super(message);
    this.uiMessage = message;
  }
}

export class CcsApiResponseError extends CcsSentryError {
  override name: string = 'CcsApiErrorResponse';
  headers?: HttpHeaders;
  status?: number;
  statusText?: string;
  url?: string | null;
  httpMethod?: string;
  error?: any;
  body: IHttpResponse<any> | null = null;

  constructor(init: {
    body: IHttpResponse<any> | null;
    message?: string;
    headers?: HttpHeaders;
    status?: number;
    statusText?: string;
    url?: string | null;
    httpMethod?: string;
    error?: any;
    name?: string;
    uiMessages?: string[];
  }) {
    super(init.message || '');

    this.headers = init.headers;
    this.status = init.status;
    this.statusText = init.statusText;
    this.url = init.url || undefined;
    this.httpMethod = init.httpMethod;
    this.error = init.error;
    this.body = init.body;
    if (init.uiMessages) this.uiMessage = join(init.uiMessages);
    if (init.name) this.name = init.name;

    const statusString = this.status?.toString() || '';
    const methodString = this.httpMethod || '';
    const urlString = (this.url && last(this.url.split('/api'))) || '';

    const bodyDetails = this.body
      ? `Body Errors: [${join(this.body.errors)}]. Body Messages: [${join(this.body.messages)}]`
      : '';

    const uiMessageDetails = this.uiMessage ? `Messages: [${this.uiMessage}]` : '';

    this.message = join([statusString, methodString, urlString, '-', bodyDetails, uiMessageDetails], '').replace(
      /([0-9]{4,})/g,
      '<id>'
    );
  }
}

export const mapHttpErrorToCcsResponseError = (
  error: HttpErrorResponse,
  method: string | undefined = undefined,
  name: string | undefined = undefined
): CcsApiResponseError => {
  let messages = [error.error, error.error?.msg, error.error?.reason];

  if (!!error.error?.errors && isArray(error.error?.errors)) {
    messages = [...messages, ...error.error.errors];
  } else if (!!error.error?.errors) {
    messages = [...messages, error.error.errors];
  }

  messages = messages.filter((m) => !!m && typeof m === 'string');

  return new CcsApiResponseError({
    error: error.error,
    headers: error.headers,
    status: error.status,
    statusText: error.statusText,
    url: error.url,
    name: name,
    httpMethod: method,
    body: null,
    message: error.message,
    uiMessages: messages.length > 0 ? messages : ['An unknown error occurred'],
  });
};

export const mapErrorToCcsSentryError = (error: Error): CcsSentryError => {
  const sentryError = new CcsSentryError(error.message);
  sentryError.name = error.name;
  sentryError.stack = error.stack;
  sentryError.uiMessage = 'An unknown error occurred';
  return sentryError;
};

export const mapHttpResponseToCcsResponseError = (
  response: any,
  method: string | undefined = undefined,
  name: string | undefined = undefined,
  message: string = 'Success flag error'
): CcsApiResponseError => {
  let messages: any[] = [];

  if (!!response?.body?.errors && isArray(response?.body?.errors)) {
    messages = [...messages, ...response.body.errors];
  }
  if (!!response?.body?.messages && isArray(response?.body?.messages)) {
    messages = [...messages, ...response.body.messages];
  }

  messages = messages.filter((m) => !!m && typeof m === 'string');

  return new CcsApiResponseError({
    error: response.error,
    headers: response.headers,
    status: response.status,
    statusText: response.statusText,
    url: response.url,
    name: name,
    httpMethod: method,
    body: response.body,
    message,
    uiMessages: messages.length > 0 ? messages : ['An unknown error occurred'],
  });
};

export const hasUnknownOrInvalidData = (state: ApiStoreData<any>): boolean => {
  return state.isLoading || !!state.error;
};

export class InvalidApiResponseDataError extends CcsSentryError {
  constructor(errors: string[]) {
    super(join(errors));

    this.name = 'InvalidApiResponseDataError';
    this.uiMessage = this.message;
  }
}

export class MissingApiRequestDataError extends CcsSentryError {
  constructor(errors: string[]) {
    super(join(errors));

    this.name = 'MissingApiRequestDataError';
    this.uiMessage = this.message;
  }
}

export interface ApiStoreData<T> {
  data: T;
  isLoading: boolean;
  isFinished: boolean;
  isUpdating: boolean;
  error: CcsSentryError | null;
  errorUpdating: CcsSentryError | null;
  requestId?: string;
}

export const defaultApiStoreData = {
  data: null,
  isLoading: false,
  isFinished: false,
  isUpdating: false,
  error: null,
  errorUpdating: null,
};

export const defaultApiStoreIterableData = {
  ...defaultApiStoreData,
  data: [],
};
